import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router";

import LicenceModal from "@client/components/containers/LicenceModal";
import DetailsTemplate from "@client/components/templates/DetailsTemplate";
import AlertModal from "@client/components/ui/AlertModal";
import Button from "@client/components/ui/Button";
import ButtonLoading from "@client/components/ui/ButtonLoading";
import { Action as DropdownActionType } from "@client/components/ui/DropdownAction";
import StatusPill from "@client/components/ui/StatusPill";
import customerStore from "@client/stores/CustomerStore";
import licenceStore from "@client/stores/LicenceStore";
import userStore from "@client/stores/LicensorStore";
import notificationStore from "@client/stores/NotificationStore";
import tagStore from "@client/stores/TagStore";
import DomUtils from "@client/utils/DomUtils";
import Style from "@client/utils/Style";
import { Customer } from "@typings/Customer";
import LicenceHelper, { Licence } from "@typings/Licence";
import { Tag } from "@typings/Tag";

import DetailsFields from "./DetailsFields";
import licenceActionBarImage from "./images/licence-action-bar.svg";

const Wrapper = styled.div`
  height: 100%;
`;
const Content = styled.div``;
const Buttons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 52px;
`;
const LeftButtons = styled.div`
  display: flex;
  flex-direction: column;
`;
const RightButtons = styled.div``;

type State = {
  showDeleteAlert: boolean;
  showLicenceModal: boolean;
};
type Props = {
  onNavigate: (path: string) => void;
  id: string;
};

@observer
class LicenceDetailsPage extends React.Component<Props, State> {
  state: State = {
    showDeleteAlert: false,
    showLicenceModal: false,
  };

  get licence(): Licence | undefined {
    const id = Number(this.props.id);
    return licenceStore.licences.find(l => Number(l.id) === id);
  }

  get customer(): Customer | undefined {
    return customerStore.customers.find(o =>
      this.licence ? o.id === this.licence.customer_id : false,
    );
  }

  get tag(): Tag | undefined {
    return tagStore.tags.find(t =>
      this.licence ? t.name === this.licence.tag : false,
    );
  }

  get name(): string {
    return LicenceHelper.getName(this.licence || null, this.customer || null);
  }

  componentDidMount() {
    licenceStore.load();
    customerStore.load();
    tagStore.load();
  }

  async handleDeleteLicence() {
    this.setState({ showDeleteAlert: false });
    if (!this.licence) {
      return;
    }
    await licenceStore.delete(this.licence.id);
    this.props.onNavigate("/");
  }

  async handleDowloadLicence() {
    if (!this.licence) {
      return;
    }
    const success = await licenceStore.generate(this.licence);
    if (!success) {
      return;
    }
    DomUtils.downloadAsTextFile(licenceStore.generatedPem, `${this.name}.pem`);
  }

  async handleSendEmail() {
    if (!this.licence || !this.customer) {
      return;
    }
    const email = this.customer.email;
    if (!email) {
      return;
    }
    const success = await licenceStore.send(this.licence.id);
    if (!success) {
      return;
    }
    notificationStore.alert(
      `An email was successfully sent to ${email}`,
      "success",
    );
  }

  renderContent() {
    return (
      <Content>
        <DetailsFields
          customer={this.customer || null}
          licence={this.licence || null}
          tag={this.tag || null}
          tokenInfo={userStore.tokenInfo}
        />
        <Buttons>
          <LeftButtons>
            {licenceStore.generating ? (
              <ButtonLoading>Generating Licence...</ButtonLoading>
            ) : (
              <Button
                primary
                onClick={() => {
                  this.handleDowloadLicence();
                }}
              >
                Download Licence
              </Button>
            )}
            {licenceStore.sending ? (
              <ButtonLoading style={{ marginTop: "16px" }}>
                Sending Email...
              </ButtonLoading>
            ) : (
              <Button
                hollow
                style={{ marginTop: "16px" }}
                onClick={() => {
                  this.handleSendEmail();
                }}
                disabled={!this.customer || !this.customer.email}
              >
                Send Licence Email
              </Button>
            )}
          </LeftButtons>
          <RightButtons>
            <Button
              highlight
              onClick={() => {
                this.setState({ showLicenceModal: true });
              }}
            >
              Recreate Licence
            </Button>
          </RightButtons>
        </Buttons>
      </Content>
    );
  }

  render() {
    let actions: DropdownActionType[] = [
      {
        label: "Download Licence",
        color: Style.palette.primary,
        action: () => {
          this.handleDowloadLicence();
        },
      },
      {
        label: "Send Licence Email",
        action: () => {
          this.handleSendEmail();
        },
      },
    ];
    actions = actions.concat([
      {
        label: "Recreate Licence",
        color: Style.palette.primaryHighlight,
        action: () => {
          this.setState({ showLicenceModal: true });
        },
      },
      {
        label: "Delete Licence",
        color: Style.palette.alert,
        disabled: true,
        action: () => {
          this.setState({ showDeleteAlert: true });
        },
      },
    ]);

    return (
      <Wrapper>
        <DetailsTemplate
          contentComponent={this.renderContent()}
          backLink="/"
          dropdownActions={actions}
          icon={licenceActionBarImage}
          title={this.name}
          subtitle={
            this.tag ? (
              <StatusPill
                label={this.tag.name}
                backgroundColor={this.tag.color}
                fontColor="white"
                borderColor={this.tag.color}
              />
            ) : null
          }
        />
        {this.state.showDeleteAlert ? (
          <AlertModal
            isOpen
            title="Delete Licence"
            message="Are you sure you want to delete the licence?"
            extraMessage="Deleting a Coriolis Licence is permanent!"
            onConfirmation={() => {
              this.handleDeleteLicence();
            }}
            onRequestClose={() => {
              this.setState({ showDeleteAlert: false });
            }}
          />
        ) : null}
        {this.state.showLicenceModal ? (
          <LicenceModal
            onRequestClose={() => {
              this.setState({ showLicenceModal: false });
            }}
            licence={this.licence}
          />
        ) : null}
      </Wrapper>
    );
  }
}

function LicenceDetailsPageWithNavigate() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  return <LicenceDetailsPage onNavigate={navigate} id={id!} />;
}

export default LicenceDetailsPageWithNavigate;
